import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ManagerSidebar from '../components/Sidebar/ManagerSidebar';

const API_BASE_URL = 'https://ikaze-thouse-backend.vercel.app/api/v1';

const GuardDashboard = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalGuards: 0,
        males: 0,
        females: 0,
        totalAttendance: 0,
        daysWith2GuardsNight: 0,
        daysWith1GuardNight: 0,
        efficiencyPercentage: 0
    });

    useEffect(() => {
        const loadStats = async () => {
            try {
                const [guardRes, attendRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/guards/`),
                    axios.get(`${API_BASE_URL}/attendance/`)
                ]);

                const guards = guardRes.data || [];
                const attendance = attendRes.data || [];

                const males = guards.filter(g => g.gender?.trim().toLowerCase() === 'male').length;
                const females = guards.filter(g => g.gender?.trim().toLowerCase() === 'female').length;

                const nightAttendance = attendance.filter(r => r.shift_type === "NIGHT");
                const nightDailyCounts = {};

                nightAttendance.forEach(record => {
                    if (!record.date) return;
                    const dateKey = record.date.includes('T') ? record.date.split('T')[0] : record.date;
                    nightDailyCounts[dateKey] = (nightDailyCounts[dateKey] || 0) + 1;
                });

                const countsPerDay = Object.values(nightDailyCounts);
                const daysWith2 = countsPerDay.filter(c => c >= 2).length;
                const daysWith1 = countsPerDay.filter(c => c === 1).length;

                const ratings = attendance.map(a => parseFloat(a.rating || 0)).filter(r => r > 0);
                let efficiency = 0;
                if (ratings.length > 0) {
                    const avg = ratings.reduce((a, b) => a + b, 0) / ratings.length;
                    efficiency = ((avg / 5) * 100).toFixed(1);
                }

                setStats({
                    totalGuards: guards.length,
                    males,
                    females,
                    totalAttendance: attendance.length,
                    daysWith2GuardsNight: daysWith2,
                    daysWith1GuardNight: daysWith1,
                    efficiencyPercentage: efficiency
                });
            } catch (err) {
                console.error("Dashboard error:", err);
            }
        };
        loadStats();
    }, []);

    return (
        <div className="dashboard-wrapper">
            <ManagerSidebar />
            
            <main className="main-content">
                {/* MODERN HEADER */}
                <header className="dashboard-header">
                    <div className="header-text">
                        <span className="badge">Operations Overview</span>
                        <h1>Security Command <span className="text-gradient">Center</span></h1>
                    </div>
                    
                    <div className="button-group">
                        <button className="btn-glass" onClick={() => navigate('/add-guard')}>
                            <span className="icon">👤</span> Add Guard
                        </button>
                        <button className="btn-primary-glow" onClick={() => navigate('/attendance')}>
                            <span className="icon">📝</span> Log Attendance
                        </button>
                        <button className="btn-glass" onClick={() => navigate('/report_guards')}>
                            <span className="icon">👤</span> Guard Report
                        </button>
                    </div>
                </header>

                {/* BENTO GRID LAYOUT */}
                <div className="bento-grid">
                    
                    {/* PRIMARY METRIC: EFFICIENCY */}
                    <div className="bento-item span-2 highlight-card">
                        <div className="card-header">
                            <span className="label">Performance Efficiency</span>
                            <div className="status-indicator live">Live</div>
                        </div>
                        <div className="efficiency-layout">
                            <div className="efficiency-value">
                                <span className="number">{stats.efficiencyPercentage}</span>
                                <span className="percent">%</span>
                            </div>
                            <div className="efficiency-track">
                                <div className="progress-fill" style={{ width: `${stats.efficiencyPercentage}%` }}>
                                    <div className="glow"></div>
                                </div>
                                <div className="markers">
                                    <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* STAFF COUNT */}
                    <div className="bento-item">
                        <span className="label">Total Force</span>
                        <div className="value-display">
                            <div className="main-val">{stats.totalGuards}</div>
                            <div className="sub-val">Active Personnel</div>
                        </div>
                        <div className="gender-split">
                            <div className="split-item">
                                <span className="dot male"></span> {stats.males} Male
                            </div>
                            <div className="split-item">
                                <span className="dot female"></span> {stats.females} Female
                            </div>
                        </div>
                    </div>

                    {/* ATTENDANCE RECORDS */}
                    <div className="bento-item">
                        <span className="label">Engagement</span>
                        <div className="value-display">
                            <div className="main-val">{stats.totalAttendance}</div>
                            <div className="sub-val">Total Logs Processed</div>
                        </div>
                        <div className="mini-chart">
                            <div className="bar" style={{height: '40%'}}></div>
                            <div className="bar" style={{height: '70%'}}></div>
                            <div className="bar" style={{height: '55%'}}></div>
                            <div className="bar" style={{height: '90%'}}></div>
                        </div>
                    </div>

                    {/* SHIFT COVERAGE (FULL) */}
                    <div className="bento-item border-emerald">
                        <div className="label-with-icon">
                            <span className="icon-sm">🛡️</span>
                            <span className="label">Optimum Coverage</span>
                        </div>
                        <div className="main-val">{stats.daysWith2GuardsNight} <span className="unit">Days</span></div>
                        <p className="description">Full strength Night Shift achieved.</p>
                    </div>

                    {/* SHIFT COVERAGE (UNDER) */}
                    <div className="bento-item border-amber">
                        <div className="label-with-icon">
                            <span className="icon-sm">⚠️</span>
                            <span className="label">Reduced Strength</span>
                        </div>
                        <div className="main-val text-amber">{stats.daysWith1GuardNight} <span className="unit">Days</span></div>
                        <p className="description">Critical: Only one guard logged.</p>
                    </div>

                </div>
            </main>

            <style>{`
                :root {
                    --bg-dark: #020617;
                    --card-bg: #0f172a;
                    --indigo-glow: #6366f1;
                    --emerald-accent: #10b981;
                    --amber-accent: #f59e0b;
                    --text-main: #f8fafc;
                    --text-dim: #94a3b8;
                }

                .dashboard-wrapper {
                    display: flex;
                    background-color: var(--bg-dark);
                    min-height: 100vh;
                    color: var(--text-main);
                    font-family: 'Plus Jakarta Sans', sans-serif;
                }

                .main-content {
                    flex: 1;
                    padding: 40px;                  
                    
                    width: 100%;
                }

                /* HEADER STYLING */
                .dashboard-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-end;
                    margin-bottom: 48px;
                    flex-wrap: wrap;
                    gap: 20px;
                }

                .badge {
                    background: rgba(99, 102, 241, 0.1);
                    color: var(--indigo-glow);
                    padding: 6px 12px;
                    border-radius: 100px;
                    font-size: 12px;
                    font-weight: 700;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                    border: 1px solid rgba(99, 102, 241, 0.2);
                }

                .header-text h1 {
                    font-size: 36px;
                    font-weight: 800;
                    margin: 12px 0 0 0;
                    letter-spacing: -1px;
                }

                .text-gradient {
                    background: linear-gradient(135deg, #818cf8 0%, #c084fc 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                }

                .button-group { display: flex; gap: 12px; }

                .btn-glass {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    color: white;
                    padding: 12px 20px;
                    border-radius: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: 0.3s;
                }

                .btn-glass:hover { background: rgba(255, 255, 255, 0.08); }

                .btn-primary-glow {
                    background: var(--indigo-glow);
                    color: white;
                    border: none;
                    padding: 12px 24px;
                    border-radius: 14px;
                    font-weight: 600;
                    box-shadow: 0 0 20px rgba(99, 102, 241, 0.4);
                    cursor: pointer;
                    transition: 0.3s;
                }

                .btn-primary-glow:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 0 30px rgba(99, 102, 241, 0.6);
                }

                /* BENTO GRID */
                .bento-grid {
                    display: grid;
                    grid-template-columns: repeat(4, 1fr);
                    grid-auto-rows: minmax(180px, auto);
                    gap: 24px;
                }

                .bento-item {
                    background: var(--card-bg);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 30px;
                    padding: 30px;
                    position: relative;
                    overflow: hidden;
                    transition: 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }

                .bento-item:hover {
                    background: #161e31;
                    border-color: rgba(255, 255, 255, 0.1);
                    transform: translateY(-5px);
                }

                .span-2 { grid-column: span 2; }

                .label {
                    color: var(--text-dim);
                    font-size: 13px;
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .main-val {
                    font-size: 48px;
                    font-weight: 800;
                    margin: 10px 0;
                }

                .unit { font-size: 18px; color: var(--text-dim); font-weight: 500; }

                /* EFFICIENCY CARD SPECIFICS */
                .highlight-card {
                    background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
                    border-left: 4px solid var(--indigo-glow);
                }

                .efficiency-layout {
                    display: flex;
                    align-items: center;
                    gap: 40px;
                    margin-top: 20px;
                }

                .efficiency-value .number { font-size: 64px; font-weight: 800; }
                .efficiency-value .percent { font-size: 24px; color: var(--indigo-glow); font-weight: 700; }

                .efficiency-track { flex: 1; }
                .progress-fill {
                    height: 12px;
                    background: var(--indigo-glow);
                    border-radius: 20px;
                    position: relative;
                }
                .glow {
                    position: absolute;
                    right: 0; top: 0; bottom: 0; width: 20px;
                    box-shadow: 0 0 15px var(--indigo-glow);
                }
                .markers {
                    display: flex; justify-content: space-between;
                    margin-top: 10px; color: var(--text-dim); font-size: 11px;
                }

                /* GENDER DOTS */
                .gender-split { display: flex; gap: 15px; margin-top: 15px; }
                .dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; margin-right: 6px; }
                .male { background: #38bdf8; box-shadow: 0 0 10px #38bdf8; }
                .female { background: #f472b6; box-shadow: 0 0 10px #f472b6; }

                /* MINI CHART ANIMATION */
                .mini-chart {
                    display: flex; align-items: flex-end; gap: 6px; height: 40px; margin-top: 15px;
                }
                .bar {
                    width: 8px; background: rgba(99, 102, 241, 0.3); border-radius: 4px;
                    animation: grow 1.5s ease-out forwards;
                }
                @keyframes grow { from { height: 0; } }

                .border-emerald { border-bottom: 4px solid var(--emerald-accent); }
                .border-amber { border-bottom: 4px solid var(--amber-accent); }
                .text-amber { color: var(--amber-accent); }

                /* RESPONSIVENESS */
                @media (max-width: 1100px) {
                    .bento-grid { grid-template-columns: repeat(2, 1fr); }
                }

                @media (max-width: 768px) {
                    .main-content { padding: 20px; }
                    .dashboard-header { flex-direction: column; align-items: flex-start; }
                    .bento-grid { grid-template-columns: 1fr; }
                    .span-2 { grid-column: span 1; }
                    .efficiency-layout { flex-direction: column; gap: 20px; }
                }
            `}</style>
        </div>
    );
};

export default GuardDashboard;